export const newTheme = {
  colors: {
    // Primary colors - black and white
    white: '#FFFFFF',
    black: '#000000',
    
    // Gray scale for text and borders
    gray: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#E5E5E5',
      300: '#D4D4D4',
      400: '#A3A3A3',
      500: '#737373',
      600: '#525252',
      700: '#404040',
      800: '#262626',
      900: '#171717',
    },
    
    // Discount/sale colors (matching mitcha.com exactly)
    discount: '#F81212', // rgb(248, 18, 18) - exact match from mitcha.com
    discountDark: '#B91C1C',
    
    // Christmas Green for hero sections and accents (Christmas theme)
    burgundy: '#0d4f0d', // Deep Christmas green for hero section, badges - Christmas theme
    
    // Sale badge red (brighter than burgundy)
    saleBadge: '#E63946', // Red for sale badges
    
    // Legacy brand colors (for old components compatibility)
    brand: {
      red: '#F81212', // rgb(248, 18, 18) - exact match from mitcha.com
      redDark: '#B91C1C',
    },
    accent: '#F81212', // rgb(248, 18, 18) - exact match from mitcha.com
    accentDark: '#B91C1C',
    error: '#F81212', // rgb(248, 18, 18) - exact match from mitcha.com
    
    // Text colors (matching mitcha.com)
    text: {
      primary: '#000000',
      secondary: '#696969', // rgb(105, 105, 105) - exact match from mitcha.com
      tertiary: '#737373',
      inverse: '#FFFFFF',
    },
    
    // Background colors
    background: {
      primary: '#FFFFFF',
      secondary: '#FAFAFA',
      tertiary: '#F5F5F5',
      dark: '#000000',
    },
    
    // Border colors
    border: {
      light: '#E5E5E5',
      medium: '#D4D4D4',
      dark: '#000000',
    },
  },
  
  typography: {
    // Editorial serif fonts for premium look + Poppins for body
    fontFamily: {
      heading: '"Playfair Display", "Bodoni Moda", Georgia, serif',
      editorial: '"Playfair Display", "Bodoni Moda", Georgia, serif',
      body: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
      nav: '"Poppins", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", Arial, sans-serif',
    },
    
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',   // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem',  // 36px
      '5xl': '3rem',     // 48px
      '6xl': '3.75rem',  // 60px
      '7xl': '5rem',     // 80px - Editorial headlines
      '8xl': '6rem',     // 96px - Large editorial headlines
      '9xl': '8rem',     // 128px - Hero headlines
    },
    
    fontWeight: {
      light: 300,
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
      black: 900,
    },
    
    lineHeight: {
      none: 1,
      tight: 1.2,
      snug: 1.375,
      normal: 1.5,
      relaxed: 1.75,
      loose: 2,
    },
    
    letterSpacing: {
      tighter: '-0.05em',
      tight: '-0.025em',
      normal: '0',
      wide: '0.025em',
      wider: '0.05em',
      widest: '0.1em',
    },
  },
  
  spacing: {
    // Clean, minimal spacing - matching mitcha.com specifications
    xs: '0.5rem',   // 8px
    sm: '1rem',     // 16px
    md: '1.5rem',   // 24px
    lg: '2rem',     // 32px
    xl: '3rem',     // 48px
    '2xl': '4rem',  // 64px
    '3xl': '5rem',  // 80px - section spacing (80-100px range)
    '4xl': '6.25rem', // 100px - section spacing (80-100px range)
    '5xl': '8rem',  // 128px (for old components)
    '6xl': '10rem', // 160px (for old components)
    // Specific mitcha.com spacing values
    sectionGap: '5rem', // 80px - spacing between major sections (80-100px)
    sectionHeaderMargin: '3rem', // 48px - section header margins (40-50px, using 48px)
    containerPadding: '5rem', // 80px - container padding on desktop (80-100px)
  },
  
  breakpoints: {
    mobile: '640px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1280px',
  },
  
  shadows: {
    // Minimal shadows
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', // for old components
  },
  
  borderRadius: {
    none: '0',
    sm: '2px',
    md: '4px',
    lg: '8px',
  },
  
  transitions: {
    fast: '150ms ease',
    base: '250ms ease',
    slow: '400ms ease',
  },
  
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
  },
  
  layout: {
    containerMaxWidth: '1400px', // 1200-1400px range, using 1400px
    headerHeight: 'auto',
    headerHeightMobile: '70px', // for old components
    announcementBarHeight: '40px',
  },
};

export type NewTheme = typeof newTheme;


